export type PromptDomain = 'email' | 'research' | 'writing' | 'planning' | 'code' | 'general';

export interface SuggestedFields {
  persona?: string[];
  context?: string[];
  task?: string[];
  sources?: string[];
  output_format?: string[];
  domain?: PromptDomain;
}

export interface BuilderDraft {
  goal: string;
  persona: string;
  context: string;
  task: string;
  sources: string;
  output_format: string;
  final_prompt: string;
  improved_prompt?: string;
}

export interface BuilderMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
}

export const BUILDER_STEPS = {
  GOAL: 0,
  PERSONA: 1,
  CONTEXT: 2,
  TASK_SOURCES: 3,
  OUTPUT_FORMAT: 4,
  COMPLETE: 5,
} as const;

export type BuilderStep = typeof BUILDER_STEPS[keyof typeof BUILDER_STEPS];

/**
 * Detect the domain/topic from the user's goal text
 */
export function detectDomain(goalText: string): PromptDomain {
  const lowerText = goalText.toLowerCase();
  
  // Email domain
  if (/\b(email|inbox|message|correspondence|reply|send|draft email)\b/.test(lowerText)) {
    return 'email';
  }
  
  // Research domain
  if (/\b(research|analyze|study|investigate|compare|report|findings|data|statistics)\b/.test(lowerText)) {
    return 'research';
  }
  
  // Writing domain
  if (/\b(write|blog|article|content|copy|script|story|creative|essay|document)\b/.test(lowerText)) {
    return 'writing';
  }
  
  // Planning domain
  if (/\b(plan|project|schedule|organize|strategy|roadmap|timeline|milestone|task list)\b/.test(lowerText)) {
    return 'planning';
  }
  
  // Code domain
  if (/\b(code|program|develop|build|debug|refactor|api|function|script|software)\b/.test(lowerText)) {
    return 'code';
  }
  
  return 'general';
}

/**
 * Get domain-specific suggestions for each step
 */
export function getDomainSuggestions(domain: PromptDomain, step: 'persona' | 'context' | 'task' | 'sources' | 'output_format'): string[] {
  const domainMap: Record<PromptDomain, Partial<Record<typeof step, string[]>>> = {
    email: {
      persona: ['executive assistant', 'email triage specialist', 'professional communicator'],
      context: ['inbox management', 'VIP contacts', 'urgency rules', 'time constraints'],
      task: ['prioritize messages', 'draft responses', 'flag follow-ups', 'summarize threads'],
      sources: ['inbox access', 'contact list', 'calendar context'],
      output_format: ['priority list', 'action items', 'email draft', 'summary table'],
    },
    research: {
      persona: ['research analyst', 'subject matter expert', 'data scientist'],
      context: ['academic focus', 'business intelligence', 'competitive analysis'],
      task: ['summarize findings', 'compare options', 'cite sources', 'identify trends'],
      sources: ['academic papers', 'industry reports', 'competitor websites'],
      output_format: ['research brief', 'comparison table', 'annotated bibliography', 'executive summary'],
    },
    writing: {
      persona: ['professional writer', 'content strategist', 'copy editor'],
      context: ['target audience', 'brand voice', 'publication platform'],
      task: ['draft content', 'edit for clarity', 'optimize for SEO', 'create outline'],
      sources: ['style guide', 'reference articles', 'brand guidelines'],
      output_format: ['blog post', 'social media copy', 'newsletter', 'long-form article'],
    },
    planning: {
      persona: ['project manager', 'strategic planner', 'operations lead'],
      context: ['project scope', 'stakeholders', 'constraints', 'timeline'],
      task: ['create roadmap', 'define milestones', 'identify risks', 'assign responsibilities'],
      sources: ['project brief', 'team capacity', 'budget details'],
      output_format: ['project plan', 'Gantt chart', 'action items', 'status report'],
    },
    code: {
      persona: ['senior developer', 'software architect', 'code reviewer'],
      context: ['tech stack', 'codebase context', 'performance requirements'],
      task: ['write function', 'debug issue', 'refactor code', 'add tests'],
      sources: ['existing codebase', 'API documentation', 'error logs'],
      output_format: ['code snippet', 'technical doc', 'PR description', 'step-by-step guide'],
    },
    general: {
      persona: ['helpful assistant', 'subject matter expert', 'professional advisor'],
      context: ['audience', 'purpose', 'constraints'],
      task: ['provide guidance', 'create content', 'analyze information'],
      sources: ['no sources needed', 'provided documents', 'web search'],
      output_format: ['bullet points', 'numbered steps', 'structured document'],
    },
  };
  
  return domainMap[domain]?.[step] || domainMap.general[step] || [];
}

/**
 * Extract potential suggestions from the user's initial goal text
 */
export function extractSuggestionsFromGoal(goalText: string): SuggestedFields {
  const suggestions: SuggestedFields = {};
  const lowerText = goalText.toLowerCase();
  
  // Detect domain first
  suggestions.domain = detectDomain(goalText);

  // Persona hints
  const personaPatterns = [
    /as (?:a|an) ([^,.]+)/i,
    /like (?:a|an) ([^,.]+)/i,
    /act as ([^,.]+)/i,
    /you are (?:a|an) ([^,.]+)/i,
  ];

  const personaSuggestions: string[] = [];
  for (const pattern of personaPatterns) {
    const match = goalText.match(pattern);
    if (match) {
      personaSuggestions.push(match[1].trim());
    }
  }

  // Add domain-specific persona suggestions
  const domainPersonas = getDomainSuggestions(suggestions.domain, 'persona');
  personaSuggestions.push(...domainPersonas.slice(0, 2));

  if (personaSuggestions.length > 0) {
    suggestions.persona = [...new Set(personaSuggestions)].slice(0, 4);
  }

  // Output format hints
  const formatPatterns: Record<string, string> = {
    'list': 'bullet points',
    'bullet': 'bullet points',
    'table': 'table format',
    'email': 'email draft',
    'report': 'structured report',
    'summary': 'concise summary',
    'step': 'numbered steps',
    'checklist': 'checklist',
    'brief': 'executive brief',
    'outline': 'detailed outline',
  };

  const formatSuggestions: string[] = [];
  for (const [keyword, format] of Object.entries(formatPatterns)) {
    if (lowerText.includes(keyword)) {
      formatSuggestions.push(format);
    }
  }
  
  // Add domain-specific format suggestions
  const domainFormats = getDomainSuggestions(suggestions.domain, 'output_format');
  formatSuggestions.push(...domainFormats.slice(0, 2));
  
  if (formatSuggestions.length > 0) {
    suggestions.output_format = [...new Set(formatSuggestions)].slice(0, 4);
  }

  // Context hints (audience, constraints)
  const contextSuggestions: string[] = [];
  const audiencePatterns = [
    /for (?:my|the|our) ([^,.]+)/i,
    /to (?:my|the|our) ([^,.]+)/i,
    /audience[:\s]+([^,.]+)/i,
  ];

  for (const pattern of audiencePatterns) {
    const match = goalText.match(pattern);
    if (match && !match[1].includes('email') && !match[1].includes('report')) {
      contextSuggestions.push(`Audience: ${match[1].trim()}`);
    }
  }
  
  // Add domain-specific context suggestions
  const domainContext = getDomainSuggestions(suggestions.domain, 'context');
  contextSuggestions.push(...domainContext.slice(0, 2));
  
  if (contextSuggestions.length > 0) {
    suggestions.context = [...new Set(contextSuggestions)].slice(0, 4);
  }

  // Task hints
  const taskSuggestions: string[] = [];
  const taskPatterns = [
    /help (?:me )?([^,.]+)/i,
    /need to ([^,.]+)/i,
    /want to ([^,.]+)/i,
    /create (?:a |an )?([^,.]+)/i,
  ];

  for (const pattern of taskPatterns) {
    const match = goalText.match(pattern);
    if (match) {
      taskSuggestions.push(match[1].trim());
    }
  }
  
  // Add domain-specific task suggestions
  const domainTasks = getDomainSuggestions(suggestions.domain, 'task');
  taskSuggestions.push(...domainTasks.slice(0, 2));
  
  if (taskSuggestions.length > 0) {
    suggestions.task = [...new Set(taskSuggestions)].slice(0, 4);
  }

  // Source hints
  const sourceSuggestions: string[] = [];
  if (lowerText.includes('no source') || lowerText.includes('without source')) {
    sourceSuggestions.push('no sources needed');
  }
  if (lowerText.includes('document') || lowerText.includes('file')) {
    sourceSuggestions.push('uploaded documents');
  }
  if (lowerText.includes('url') || lowerText.includes('link') || lowerText.includes('website')) {
    sourceSuggestions.push('web links');
  }
  
  // Add domain-specific source suggestions
  const domainSources = getDomainSuggestions(suggestions.domain, 'sources');
  sourceSuggestions.push(...domainSources.slice(0, 2));
  
  if (sourceSuggestions.length > 0) {
    suggestions.sources = [...new Set(sourceSuggestions)].slice(0, 4);
  }

  return suggestions;
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create initial assistant message for Step 0
 */
export function getInitialAssistantMessage(): BuilderMessage {
  return {
    id: generateMessageId(),
    role: 'assistant',
    content: "What are you trying to accomplish? A sentence (or a paragraph) is perfect.",
    timestamp: new Date(),
  };
}

/**
 * Check if the conversation is complete based on the final prompt marker
 */
export function isConversationComplete(messages: BuilderMessage[]): boolean {
  const lastAssistantMessage = [...messages]
    .reverse()
    .find(m => m.role === 'assistant');
  
  if (!lastAssistantMessage) return false;
  
  const content = lastAssistantMessage.content;
  
  // Check for completion patterns (both bracketed and plain text formats)
  return content.includes('[ROLE / PERSONA]') ||
         content.includes('ROLE / PERSONA:') ||
         content.includes('ROLE / PERSONA\n') ||
         content.includes('Copy/Paste Prompt') ||
         content.includes('## Final Prompt') ||
         // Also check for multiple sections being present (indicates final prompt)
         (content.includes('PERSONA') && 
          content.includes('CONTEXT') && 
          content.includes('TASK') && 
          content.includes('OUTPUT FORMAT'));
}

/**
 * Extract the final prompt from the completion message and clean markdown formatting
 */
export function extractFinalPrompt(completionMessage: string): string {
  // Look for the structured prompt sections (with or without brackets/markdown)
  const patterns = [
    /\[ROLE\s*\/?\s*PERSONA\]/i,
    /\*\*\[ROLE\s*\/?\s*PERSONA\]\*\*/i,
    /ROLE\s*\/?\s*PERSONA/i,
  ];
  
  let promptStart = -1;
  for (const pattern of patterns) {
    const match = completionMessage.match(pattern);
    if (match && match.index !== undefined) {
      promptStart = match.index;
      break;
    }
  }
  
  if (promptStart !== -1) {
    // Find where the prompt ends (before "Optional Upgrades" or similar)
    const upgradesStart = completionMessage.indexOf('Optional Upgrades', promptStart);
    const checklistStart = completionMessage.indexOf('Quality Checklist', promptStart);
    
    const endIndex = Math.min(
      upgradesStart !== -1 ? upgradesStart : completionMessage.length,
      checklistStart !== -1 ? checklistStart : completionMessage.length
    );
    
    let prompt = completionMessage.slice(promptStart, endIndex).trim();
    
    // Strip markdown formatting
    prompt = prompt
      .replace(/\*\*\[([^\]]+)\]\*\*/g, '$1:')  // **[LABEL]** → LABEL:
      .replace(/\[([^\]]+)\]/g, '$1:')           // [LABEL] → LABEL:
      .replace(/\*\*([^*]+)\*\*/g, '$1')         // **text** → text
      .replace(/^\s*#+\s*/gm, '')                // Remove heading markers
      .replace(/^(ROLE\s*\/?\s*PERSONA)\s*$/gm, '$1:')  // Add colon if missing
      .replace(/^(CONTEXT\s*&?\s*GOAL)\s*$/gm, '$1:')
      .replace(/^(TASK)\s*$/gm, '$1:')
      .replace(/^(SOURCES)\s*$/gm, '$1:')
      .replace(/^(OUTPUT\s*FORMAT)\s*$/gm, '$1:')
      .replace(/^(CONSTRAINTS\s*&?\s*QUALITY\s*BAR)\s*$/gm, '$1:')
      .replace(/^(CLARIFYING\s*QUESTIONS.*)\s*$/gm, '$1:')
      .trim();
    
    return prompt;
  }
  
  // Fallback: clean the whole message
  return completionMessage
    .replace(/\*\*\[([^\]]+)\]\*\*/g, '$1:')
    .replace(/\[([^\]]+)\]/g, '$1:')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^\s*#+\s*/gm, '')
    .trim();
}

/**
 * Detect which step the AI is currently asking about.
 * Only matches explicit step headers (e.g. "Step 1:", "**Step 2:**") to avoid
 * false positives from keyword matching on normal conversation text.
 * Returns null when no explicit header is found (no step change detected).
 */
export function detectCurrentStep(content: string): 'persona' | 'context' | 'task' | 'sources' | 'output_format' | null {
  // Only match explicit step headers — required by the system prompt
  // Supports formats like "Step 1:", "**Step 1:**", "**Step 1: AI Persona**", "Step 1 —"
  const match = content.match(/\*{0,2}step\s+([1-4])\s*[:\u2014—]/i);
  if (!match) return null;

  const stepNum = parseInt(match[1], 10);
  switch (stepNum) {
    case 1: return 'persona';
    case 2: return 'context';
    case 3: return 'task';
    case 4: return 'output_format';
    default: return null;
  }
}

/**
 * Prefix used in tool-selection user messages (e.g. "Optimize for ChatGPT (GPT-5.4)")
 */
export const TOOL_SELECT_PREFIX = 'Optimize for ';

/**
 * Storage key for mode persistence
 */
export const PROMPT_INPUT_MODE_KEY = 'prompt_input_mode';

export interface GuidedBuilderResponse {
  message: string;
  options: string[];
  step: number | 'complete';
  final_prompt: string | null;
}

export interface StepSelection {
  step: number;
  value: string;
}

/**
 * Append the builder conversation transcript below the final prompt as additional context.
 */
export function appendConversationContext(
  finalPrompt: string,
  messages: BuilderMessage[]
): string {
  const transcript = messages
    .filter(m => m.role === 'user' || m.role === 'assistant')
    .filter(m => !m.content.includes("I'm now building your prompt"))
    .map(m => `**${m.role === 'user' ? 'You' : 'Cosmo'}:** ${m.content}`)
    .join('\n\n');

  if (!transcript) return finalPrompt;

  return `${finalPrompt}\n\n---\n## Additional Context\nThe following conversation was used to develop this prompt:\n\n${transcript}`;
}

export type PromptInputMode = 'build' | 'improve' | 'workflow';

export function getStoredMode(): PromptInputMode {
  if (typeof window === 'undefined') return 'build';
  const stored = localStorage.getItem(PROMPT_INPUT_MODE_KEY);
  if (stored === 'improve' || stored === 'workflow') return stored;
  return 'build';
}

export function setStoredMode(mode: PromptInputMode): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PROMPT_INPUT_MODE_KEY, mode);
}
